import type {render as RenderEquation} from 'katex';
import React, {RefObject, useEffect} from 'react';
import {useRef} from 'react';

export enum EquationDisplayMode {
  INLINE = 'INLINE',
  BLOCK = 'BLOCK',
}

export const latexEquationStubbableFunctions: {renderEquation: typeof RenderEquation | null} = {
  renderEquation: null,
};

export interface LatexEquationProps {
  equation: string;
  displayMode: EquationDisplayMode;
}

/**
 * Component to render mathematical markup, using the Katex library.
 *
 * The math markup is very similar to Latex, and in most cases you can just use this component as if it
 * renders Latex. You will not be able to render Latex commands, such as \equation or \align.
 *
 * See Katex.org for more.
 *
 * @equation Latex string to render.
 * @displayMode Using INLINE will result in a span element rendering; BLOCK will result in a div rendering.
 * @note When testing, this will not render using Katex but rather a custom-defined function, since
 * Katex does not work in test mode. See testSetup.ts for more.
 */
export default function LatexEquation({equation, displayMode}: LatexEquationProps): JSX.Element {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    import(/* webpackChunkName: "katex" */ 'katex').then((katex) => {
      if (ref.current) {
        (latexEquationStubbableFunctions.renderEquation ?? katex.default.render)(equation, ref.current, {
          throwOnError: false,
        });
      }
    });
  }, [equation, displayMode]);

  if (displayMode === EquationDisplayMode.INLINE) {
    return <span ref={ref as RefObject<HTMLSpanElement>} role="math" />;
  } else {
    return <div ref={ref as RefObject<HTMLDivElement>} role="math" />;
  }
}
